import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { Link, router } from '@inertiajs/react'
import { AppLayout } from '@/components/layouts/AppLayout'
import { Markdown } from '@/components/Markdown'

interface AgentRef {
  id: number
  username: string
  slug: string
}

interface NoteDetail {
  id: number
  title: string
  content: string | null
  folder_id: number | null
  folder_name: string | null
  created_by: AgentRef
  updated_by: AgentRef
  created_at: string
  updated_at: string
}

interface Props {
  notebook: { id: number; name: string; slug: string }
  note: NoteDetail
  can_edit: boolean
}

export default function Show({ notebook, note, can_edit }: Props) {
  function handleDelete() {
    if (confirm(`Delete note '${note.title}'? This cannot be undone.`)) {
      router.delete(`/notebooks/notebooks/${notebook.slug}/notes/${note.id}`)
    }
  }

  return (
    <AppLayout title={note.title} maxWidth="md">
      <Box sx={{ mb: 2 }}>
        <Button
          component={Link}
          href={`/notebooks/notebooks/${notebook.slug}`}
          size="small"
        >
          Back to {notebook.name}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {note.folder_name && (
            <Chip label={note.folder_name} size="small" variant="outlined" />
          )}
          <Typography variant="caption" color="text.secondary">
            Created by {note.created_by.username} on {new Date(note.created_at).toLocaleDateString()}
          </Typography>
          {note.updated_by.id !== note.created_by.id && (
            <Typography variant="caption" color="text.secondary">
              Updated by {note.updated_by.username}
            </Typography>
          )}
        </Box>
        {can_edit && (
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button
              component={Link}
              href={`/notebooks/notebooks/${notebook.slug}/notes/${note.id}/edit`}
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Box>
        )}
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 4 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        {note.content ? (
          <Markdown>{note.content}</Markdown>
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            This note has no content yet.
          </Typography>
        )}
      </Paper>
    </AppLayout>
  )
}
